import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatDialogRef } from '@angular/material/dialog';
import { EstablishmentService } from '../../../services/establishment/establishment.service';
import { ToastService } from '../../../services/toast/toast.service';
import { LoaderComponent } from '../../../atoms/loader/loader.component';

// Book a mandir utsav on behalf of a devotee. Two modes:
// - 'ACCOUNT': the devotee has an account -> find the family by family code or
//   aadhaar, pick the primary requestor, members and saved visitors, add new guests.
// - 'NO_ACCOUNT': the guest has no account -> the booking is made in the name of a
//   placeholder (me, or any active devotee), who only accompanies and is not a
//   guest. All guests are added as new guests, plus the guest's contact number.
// Closes with `true` when a booking is created.
@Component({
  selector: 'app-manual-booking-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatFormFieldModule, MatSelectModule, LoaderComponent],
  templateUrl: './manual-booking-dialog.component.html',
  styleUrls: ['./manual-booking-dialog.component.scss', '../../../../common-style/ac-dialog.scss'],
})
export class ManualBookingDialogComponent implements OnInit {
  utsavs: any[] = [];
  today: string = '';
  utsavId: number | null = null;
  fromDate: string = '';
  toDate: string = '';

  mode: 'ACCOUNT' | 'NO_ACCOUNT' = 'ACCOUNT';
  searchBy: 'familyCode' | 'aadhaarNumber' = 'familyCode';
  searchText: string = '';

  family: any = null; // { familyCode, members, visitors }
  primaryDevoteeId: number | null = null;
  selectedMemberIds = new Set<number>();
  selectedVisitorIds = new Set<number>();

  newGuests: any[] = [];
  newGuest: any = this.emptyGuest();
  guestContact: string = '';

  availabilityMessage: string = '';
  isLoading: boolean = false;

  readonly genderOptions = [
    { value: 'MALE', label: 'Male' },
    { value: 'FEMALE', label: 'Female' },
    { value: 'OTHERS', label: 'Others' },
  ];

  // the same relationship lists as the devotee screens (gender specific)
  readonly maleRelations: string[] = [
    'Father', 'Son', 'Brother', 'Husband', 'Son-In-Law', 'Brother-In-Law',
    'Grand-Father', 'Grand-Son', 'Uncle', 'Cousin', 'Relative Others',
    'GuruBhai Attached with Mandir', 'GuruBhai Others', 'Non-Initiated Male',
  ];
  readonly femaleRelations: string[] = [
    'Mother', 'Daughter', 'Sister', 'Wife', 'Daughter-In-Law', 'Sister-In-Law',
    'Grand-Mother', 'Grand-Daughter', 'Aunt', 'Cousin', 'Relative Others',
    'GuruBhai Attached with Mandir', 'GuruBhai Others', 'Non-Initiated Female',
  ];
  readonly otherRelations: string[] = [
    'Cousin', 'Relative Others', 'GuruBhai Attached with Mandir', 'GuruBhai Others',
  ];

  constructor(
    public dialogRef: MatDialogRef<ManualBookingDialogComponent>,
    private establishmentService: EstablishmentService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.establishmentService.listManualBookingUtsavs().subscribe({
      next: (res: any) => {
        this.utsavs = res?.data?.utsavs || [];
        this.today = res?.data?.today || '';
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to load utsavs.');
      },
    });
  }

  get selectedUtsav(): any {
    return this.utsavs.find((u) => u.utsavId === this.utsavId) || null;
  }

  // first day that can be booked: accommodation start, or today if it has started
  get minDate(): string {
    const utsav = this.selectedUtsav;
    if (!utsav) return '';
    return utsav.accommodationStartDate > this.today ? utsav.accommodationStartDate : this.today;
  }

  get maxDate(): string {
    return this.selectedUtsav?.accommodationEndDate || '';
  }

  onUtsavChange(): void {
    this.fromDate = this.minDate;
    this.toDate = this.maxDate;
    this.availabilityMessage = '';
  }

  setMode(mode: 'ACCOUNT' | 'NO_ACCOUNT'): void {
    if (this.mode === mode) return;
    this.mode = mode;
    this.clearFamily();
    this.guestContact = '';
    // SELF is only for a guest without an account
    this.newGuests.forEach((g) => {
      if (g.coVisitorRelationship === 'SELF') g.coVisitorRelationship = '';
    });
  }

  get hasSelfGuest(): boolean {
    return this.newGuests.some((g) => g.coVisitorRelationship === 'SELF');
  }

  // relationships for the guest being added; SELF once, and only without an account
  get relationOptions(): string[] {
    const gender = String(this.newGuest.coVisitorGender || '').toUpperCase();
    const list =
      gender === 'MALE' ? this.maleRelations : gender === 'FEMALE' ? this.femaleRelations : this.otherRelations;
    return this.mode === 'NO_ACCOUNT' && !this.hasSelfGuest ? ['SELF', ...list] : list;
  }

  // a changed gender may make the picked relationship invalid
  onGuestGenderChange(): void {
    if (!this.relationOptions.includes(this.newGuest.coVisitorRelationship)) {
      this.newGuest.coVisitorRelationship = '';
    }
  }

  // -- find the family (or the placeholder) --

  search(): void {
    const text = this.searchText.trim();
    if (this.searchBy === 'familyCode' && !/^\d{12}$/.test(text)) {
      this.toastService.error('Enter a valid 12 digit family code.');
      return;
    }
    if (this.searchBy === 'aadhaarNumber' && !/^[2-9]\d{11}$/.test(text)) {
      this.toastService.error('Enter a valid 12 digit Aadhaar number.');
      return;
    }
    this.loadFamily({ [this.searchBy]: text });
  }

  // book in the signed-in staff member's own name (placeholder mode)
  useMyName(): void {
    this.loadFamily({ self: true });
  }

  private loadFamily(search: { familyCode?: string; aadhaarNumber?: string; self?: boolean }): void {
    this.clearFamily();
    this.isLoading = true;
    this.establishmentService.getManualBookingFamily(search).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        if (res?.data?.members?.length) {
          this.family = res.data;
        } else {
          this.toastService.error(res?.message || 'No active devotee found.');
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to find the family.');
      },
    });
  }

  private clearFamily(): void {
    this.family = null;
    this.primaryDevoteeId = null;
    this.selectedMemberIds.clear();
    this.selectedVisitorIds.clear();
    this.availabilityMessage = '';
  }

  // in ACCOUNT mode the primary requestor is always a guest too
  selectPrimary(member: any): void {
    if (!member.isAdult) return;
    this.primaryDevoteeId = member.devoteeId;
    if (this.mode === 'ACCOUNT') this.selectedMemberIds.add(member.devoteeId);
  }

  toggleMember(member: any): void {
    if (this.selectedMemberIds.has(member.devoteeId)) {
      if (member.devoteeId === this.primaryDevoteeId) return;
      this.selectedMemberIds.delete(member.devoteeId);
    } else {
      this.selectedMemberIds.add(member.devoteeId);
    }
    this.availabilityMessage = '';
  }

  toggleVisitor(visitor: any): void {
    if (this.selectedVisitorIds.has(visitor.visitorId)) this.selectedVisitorIds.delete(visitor.visitorId);
    else this.selectedVisitorIds.add(visitor.visitorId);
    this.availabilityMessage = '';
  }

  // -- new guests (not saved in any visitor list) --

  private emptyGuest(): any {
    return {
      coVisitorFullName: '',
      coVisitorDateOfBirth: '',
      coVisitorGender: '',
      coVisitorIdentityRefNum: '',
      coVisitorMobile: '',
      coVisitorRelationship: '',
    };
  }

  addGuest(): void {
    const g = this.newGuest;
    if (!g.coVisitorFullName.trim()) return this.toastService.error("Enter the guest's full name.");
    if (!g.coVisitorDateOfBirth) return this.toastService.error("Enter the guest's date of birth.");
    if (!g.coVisitorGender) return this.toastService.error("Select the guest's gender.");
    if (!/^[2-9]\d{11}$/.test(g.coVisitorIdentityRefNum.trim()))
      return this.toastService.error('Enter a valid 12 digit Aadhaar number.');
    if (!/^[5-9]\d{9}$/.test(g.coVisitorMobile.trim()))
      return this.toastService.error('Enter a valid 10 digit mobile number.');
    if (this.mode === 'NO_ACCOUNT' && g.coVisitorRelationship === 'SELF' && !this.guestContact) {
      this.guestContact = g.coVisitorMobile.trim();
    }
    this.newGuests.push({
      ...g,
      coVisitorFullName: g.coVisitorFullName.trim(),
      coVisitorIdentityRefNum: g.coVisitorIdentityRefNum.trim(),
      coVisitorMobile: g.coVisitorMobile.trim(),
    });
    this.newGuest = this.emptyGuest();
    this.availabilityMessage = '';
  }

  removeGuest(index: number): void {
    this.newGuests.splice(index, 1);
    this.availabilityMessage = '';
  }

  // -- guest counts (from the people picked) --

  private get guestPeople(): Array<{ gender: string; dateOfBirth: string }> {
    const members = (this.family?.members || []).filter((m: any) => this.selectedMemberIds.has(m.devoteeId));
    const visitors = (this.family?.visitors || []).filter((v: any) => this.selectedVisitorIds.has(v.visitorId));
    return [
      ...members.map((m: any) => ({ gender: m.gender, dateOfBirth: m.dateOfBirth })),
      ...visitors.map((v: any) => ({ gender: v.gender, dateOfBirth: v.dateOfBirth })),
      ...this.newGuests.map((g: any) => ({ gender: g.coVisitorGender, dateOfBirth: g.coVisitorDateOfBirth })),
    ];
  }

  // under 12 counts as a child (the same age rule as the primary requestor)
  get counts(): { male: number; female: number; children: number; total: number } {
    let male = 0;
    let female = 0;
    let children = 0;
    for (const p of this.guestPeople) {
      if (p.dateOfBirth && this.ageOf(p.dateOfBirth) < 12) children++;
      else if (String(p.gender || '').toUpperCase() === 'FEMALE') female++;
      else male++;
    }
    return { male, female, children, total: male + female + children };
  }

  private ageOf(dob: string): number {
    const birth = new Date(dob);
    const now = new Date();
    let age = now.getFullYear() - birth.getFullYear();
    const hadBirthday =
      now.getMonth() > birth.getMonth() ||
      (now.getMonth() === birth.getMonth() && now.getDate() >= birth.getDate());
    if (!hadBirthday) age--;
    return age;
  }

  // -- check + submit --

  private validate(): string {
    if (!this.utsavId) return 'Select an utsav.';
    if (!this.fromDate || !this.toDate) return 'Select the stay dates.';
    if (this.fromDate > this.toDate) return 'From date cannot be after To date.';
    if (this.fromDate < this.minDate || this.toDate > this.maxDate)
      return `Stay dates must be between ${this.minDate} and ${this.maxDate}.`;
    if (!this.family) return 'Find the family first.';
    if (!this.primaryDevoteeId)
      return this.mode === 'ACCOUNT' ? 'Select the primary requestor.' : 'Select the person to book in the name of.';
    if (this.mode === 'NO_ACCOUNT' && !/^[5-9]\d{9}$/.test(this.guestContact.trim()))
      return "Enter the guest's 10 digit contact number.";
    if (this.counts.total < 1) return 'Add at least one guest.';
    if (this.mode === 'NO_ACCOUNT' && !this.hasSelfGuest)
      return 'Mark one guest with the relationship SELF (the guest the booking is for).';
    return '';
  }

  checkAvailability(): void {
    const error = this.validate();
    if (error) {
      this.toastService.error(error);
      return;
    }
    this.isLoading = true;
    this.establishmentService.checkManualBookingAvailability(this.utsavId!, this.counts.total).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        this.availabilityMessage = res?.data?.available
          ? `Available. ${res.data.freePlaces} place(s) free.`
          : `Not available. Only ${res?.data?.freePlaces ?? 0} place(s) free.`;
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to check availability.');
      },
    });
  }

  submit(): void {
    const error = this.validate();
    if (error) {
      this.toastService.error(error);
      return;
    }
    const counts = this.counts;
    const payload = {
      utsavId: this.utsavId,
      fromDate: this.fromDate,
      toDate: this.toDate,
      isAccompaniedByOnly: this.mode === 'NO_ACCOUNT',
      guestRequestorContact: this.mode === 'NO_ACCOUNT' ? this.guestContact.trim() : null,
      familyCode: this.family.familyCode,
      primaryDevoteeId: this.primaryDevoteeId,
      devoteeIds: this.mode === 'ACCOUNT' ? [...this.selectedMemberIds] : [],
      visitorIds: this.mode === 'ACCOUNT' ? [...this.selectedVisitorIds] : [],
      unsavedVisitorDetails: this.newGuests,
      maleGuestCount: counts.male,
      femaleGuestCount: counts.female,
      childrenGuestCount: counts.children,
    };
    this.isLoading = true;
    this.establishmentService.createManualBooking(payload).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        this.toastService.success(res?.message || 'Booking created.');
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.error(err?.error?.message || 'Failed to create the booking.');
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
